import { API_BASE_URL } from './config';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Target, Flag, CheckCircle } from 'lucide-react';
import secureStorage from './utils/secureStorage';

const API = API_BASE_URL;

const DisplayObjectives = () => {
  const [objectives, setObjectives] = useState([]);
  const [exerciseName, setExerciseName] = useState('');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  const loadObjectives = async () => {
    try {
      const token = secureStorage.getToken();
      
      if (!token) {
        console.error('No authentication token found');
        setLoading(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Try to get exercise ID from localStorage (set by main app when viewing exercise)
      let exerciseId = secureStorage.getPreference('current-exercise-id');
      let exerciseName = secureStorage.getPreference('current-exercise-name');
      
      console.log('Exercise ID from localStorage:', exerciseId);
      console.log('Exercise name from localStorage:', exerciseName);
      
      // If not in localStorage, try to get from URL hash
      if (!exerciseId) {
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1]);
        exerciseId = urlParams.get('exercise');
        console.log('Exercise ID from URL:', exerciseId);
      }
      
      // If still no exercise ID, fall back to current exercise API
      if (!exerciseId) {
        console.log('No exercise ID found, fetching current exercise...');
        const exerciseResponse = await axios.get(`${API}/api/exercises/current`, { headers });
        console.log('Exercise response:', exerciseResponse.data);
        
        if (exerciseResponse.data) {
          exerciseId = exerciseResponse.data.id;
          exerciseName = exerciseResponse.data.exercise_name;
        }
      }
      
      if (exerciseId) {
        setExerciseName(exerciseName || 'Exercise Objectives');
        
        // Load objectives for this specific exercise
        console.log('Fetching objectives for exercise:', exerciseId);
        const objectivesResponse = await axios.get(
          `${API}/api/hierarchical-objectives?exercise_id=${exerciseId}`,
          { headers }
        );
        console.log('Objectives response:', objectivesResponse.data);
        console.log('Number of objectives:', objectivesResponse.data?.length || 0);
        
        setObjectives(objectivesResponse.data || []);
      } else {
        console.log('No exercise ID available');
      }
      
      setLoading(false);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error loading objectives:', error);
      console.error('Error details:', error.response?.data || error.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadObjectives();

    // Auto-refresh every 120 seconds
    const refreshInterval = setInterval(() => {
      loadObjectives();
    }, 120000);

    return () => clearInterval(refreshInterval);
  }, []);

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'border-green-500 bg-green-900/20',
      medium: 'border-yellow-500 bg-yellow-900/20',
      high: 'border-orange-500 bg-orange-900/20',
      critical: 'border-red-500 bg-red-900/20'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'text-green-400',
      pending: 'text-yellow-400',
      completed: 'text-blue-400',
      archived: 'text-gray-400'
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="w-screen h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading Objectives...</div>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen bg-gray-900 overflow-hidden">
      {/* Header Bar */}
      <div className="bg-gray-800 border-b border-orange-500 px-6 py-4 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-orange-500">EXRSIM - Incident Objectives</h1>
          <p className="text-lg text-gray-400">{exerciseName}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-400">Last Updated</p>
          <p className="text-xl text-white">{lastUpdate.toLocaleTimeString()}</p>
        </div>
      </div>

      {/* Objectives Content */}
      <div className="p-6 h-[calc(100vh-100px)] overflow-y-auto">
        {objectives.length === 0 ? (
          <div className="text-center text-gray-400 py-20">
            <Target className="h-24 w-24 mx-auto mb-4 opacity-50" />
            <p className="text-2xl">No objectives defined for this exercise</p>
          </div>
        ) : (
          <div className="space-y-6">
            {objectives.map((objective, objIdx) => (
              <div key={objective.id} className={`border-2 rounded-lg ${getPriorityColor(objective.priority)}`}>
                {/* Objective */}
                <div className="p-6">
                  <div className="flex items-start gap-4">
                    <Target className="h-10 w-10 text-orange-500 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-4 mb-3">
                        <h2 className="text-3xl font-bold text-white">
                          Objective {objIdx + 1}: {objective.title}
                        </h2>
                        <span className={`text-lg px-3 py-1 rounded border-2 ${getPriorityColor(objective.priority)} uppercase font-bold`}>
                          {objective.priority}
                        </span>
                        <span className={`text-lg font-semibold ${getStatusColor(objective.status)} uppercase`}>
                          {objective.status}
                        </span>
                      </div>
                      <p className="text-gray-300 text-xl mb-4">{objective.description}</p>
                      <div className="grid grid-cols-2 gap-4 text-lg">
                        {objective.assigned_to && (
                          <p className="text-gray-400">
                            <span className="font-semibold text-white">Assigned to:</span> {objective.assigned_to}
                          </p>
                        )}
                        {objective.deadline && (
                          <p className="text-gray-400">
                            <span className="font-semibold text-white">Deadline:</span> {objective.deadline}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategies */}
                {objective.strategies && objective.strategies.length > 0 && (
                  <div className="ml-16 mr-6 pb-6 space-y-4">
                    {objective.strategies.map((strategy, stratIdx) => (
                      <div key={strategy.id} className="bg-gray-800/50 border-2 border-gray-700 rounded-lg">
                        <div className="p-5">
                          <div className="flex items-start gap-3">
                            <Flag className="h-8 w-8 text-yellow-500 flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <h3 className="text-2xl font-semibold text-white mb-2">
                                Strategy {objIdx + 1}.{stratIdx + 1}: {strategy.title}
                              </h3>
                              <p className="text-gray-300 text-lg mb-3">{strategy.description}</p>
                              <div className="grid grid-cols-2 gap-3 text-base">
                                {strategy.resource_requirements && (
                                  <p className="text-gray-400">
                                    <span className="font-semibold text-white">Resources:</span> {strategy.resource_requirements}
                                  </p>
                                )}
                                {strategy.timeline && (
                                  <p className="text-gray-400">
                                    <span className="font-semibold text-white">Timeline:</span> {strategy.timeline}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Tactics */}
                        {strategy.tactics && strategy.tactics.length > 0 && (
                          <div className="ml-14 mr-5 pb-5 space-y-3">
                            {strategy.tactics.map((tactic, tactIdx) => (
                              <div key={tactic.id} className="bg-gray-700/50 border border-gray-600 rounded p-4">
                                <div className="flex items-start gap-3">
                                  <CheckCircle className={`h-6 w-6 flex-shrink-0 mt-1 ${
                                    tactic.status === 'completed' ? 'text-green-500' : 
                                    tactic.status === 'in-progress' ? 'text-yellow-500' : 
                                    'text-gray-400'
                                  }`} />
                                  <div className="flex-1">
                                    <p className="font-semibold text-white text-xl mb-2">
                                      Tactic {objIdx + 1}.{stratIdx + 1}.{tactIdx + 1}: {tactic.action}
                                    </p>
                                    <div className="grid grid-cols-3 gap-3 text-sm text-gray-300">
                                      {tactic.responsible_party && (
                                        <p><span className="font-semibold text-white">Responsible:</span> {tactic.responsible_party}</p>
                                      )}
                                      {tactic.completion_date && (
                                        <p><span className="font-semibold text-white">Due:</span> {tactic.completion_date}</p>
                                      )}
                                      <p>
                                        <span className="font-semibold text-white">Status:</span>{' '}
                                        <span className={
                                          tactic.status === 'completed' ? 'text-green-400' : 
                                          tactic.status === 'in-progress' ? 'text-yellow-400' : 
                                          'text-gray-400'
                                        }>
                                          {tactic.status}
                                        </span>
                                      </p>
                                    </div>
                                    {tactic.notes && (
                                      <p className="text-gray-400 text-base mt-2">{tactic.notes}</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DisplayObjectives;
